/**
 * The underground garage of a building with `style.garage`: a ramp cut into the ground beside
 * the building, reaching a rolling gate under the wall. Pure geometry of the ramp footprint, shared
 * by the renderer (the ground gets a hole there), the walker (the pit is fenced), and the placement
 * rule on the client (blueprint ghost) and on the server (`WorldView.checkPlacement`).
 */
export const RAMP_LENGTH = 12;
export const RAMP_WIDTH = 4.6;
export const RAMP_DEPTH = 3.4;
/** Concrete curb around the opening: it keeps the walker out and cars from rolling in. */
export const RAMP_CURB = 0.4;
/** Outward unit direction (x, z) of each side in the building's local frame. */
const SIDE_NORMAL = {
    n: [0, -1],
    s: [0, 1],
    e: [1, 0],
    w: [-1, 0],
};
/** The ramp of a building that has a garage, in the building's frame. */
export function garageRamp(b) {
    const [nx, nz] = SIDE_NORMAL[b.doorSide ?? "s"];
    // Out through the wall to the left of someone standing at the door looking in.
    const ux = -nz;
    const uz = nx;
    const along = Math.abs(nx) > 0 ? b.width : b.depth; // extent of the building along the door normal
    const across = Math.abs(nx) > 0 ? b.depth : b.width; // extent across it (the wall the ramp meets)
    const wallDist = across / 2;
    // Toward the back of that wall, clear of the door and of the corner.
    const slide = Math.max(0, along / 2 - RAMP_WIDTH / 2 - 2.2);
    return {
        cx: ux * (wallDist + RAMP_LENGTH / 2) - nx * slide,
        cz: uz * (wallDist + RAMP_LENGTH / 2) - nz * slide,
        yaw: Math.atan2(-uz, ux),
        length: RAMP_LENGTH,
        width: RAMP_WIDTH,
        depth: RAMP_DEPTH,
    };
}
/** The id of a building's ramp among the footprints of a placement world. */
export const rampIdOf = (buildingId) => `${buildingId}~ramp`;
/** The ramp (with its curb) as a placement footprint for a pose of the building. */
export function rampFootprint(b, pose) {
    const g = garageRamp(b);
    const c = Math.cos(pose.yawRad);
    const s = Math.sin(pose.yawRad);
    return {
        x: pose.x + g.cx * c + g.cz * s,
        z: pose.z - g.cx * s + g.cz * c,
        yawRad: pose.yawRad + g.yaw,
        width: g.length + 2 * RAMP_CURB,
        depth: g.width + 2 * RAMP_CURB,
    };
}
//# sourceMappingURL=garage.js.map